"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

import {
  DatasetDurationManager,
  DatasetSizeManager,
} from "@/services/license/base/license-manager-abstractions";
import { MediaInputStorage } from "@/services/storage/base/media-input-storage";

export type UploadFileTab = "youtube" | "localFile" | "recordAudio";

export type YouTubeSongData = {
  type: "youtube";
  songFile?: File;
  title?: string;
  image?: string;
};

export type LocalFileSongData = {
  type: "localFile";
  songFile?: File;
};

export type RecordAudioSongData = {
  type: "recordAudio";
  songFile?: File;
};

export type UploadedFiles = {
  activeTab: UploadFileTab;
  youtube?: YouTubeSongData;
  localFile?: LocalFileSongData;
  recordAudio?: RecordAudioSongData;
};

export interface MediaInputContextState {
  uploadedFiles: UploadedFiles;
  handleFileChange: (
    data: LocalFileSongData | YouTubeSongData | RecordAudioSongData,
  ) => void;
  setSelectedTab: (value: UploadFileTab) => void;
  licenseManager: DatasetSizeManager & DatasetDurationManager;
}

const MediaInputContext = createContext<MediaInputContextState | null>(null);

export const useMediaInputContext = () => {
  const mediaInputContext = useContext(MediaInputContext);

  if (!mediaInputContext) {
    throw new Error(
      "useMediaInputContext must be used within a MediaInputProvider",
    );
  }

  return mediaInputContext;
};

const MediaInputProvider: React.FC<{
  sessionStorage: MediaInputStorage;
  licenseManager: DatasetSizeManager & DatasetDurationManager;
  children: React.ReactNode;
}> = ({ sessionStorage, licenseManager, children }) => {
  const initialUploadedFiles = {
    activeTab: "localFile",
    youtube: undefined,
    localFile: undefined,
    recordAudio: undefined,
  } as UploadedFiles;

  const [uploadedFiles, setUploadedFiles] =
    useState<UploadedFiles>(initialUploadedFiles);

  const handleFileChange = async (
    data: LocalFileSongData | YouTubeSongData | RecordAudioSongData,
  ) => {
    switch (data.type) {
      case "localFile":
        await sessionStorage.localSongDataStorage.setLocalSongData(
          data.songFile ?? null,
        );
        await sessionStorage.selectedTabStorage.setSelectedFileTab("localFile");
        setUploadedFiles((prevState) => ({
          ...prevState,
          localFile: data,
        }));
        break;
      case "youtube":
        if (data.songFile !== undefined) {
          await sessionStorage.linkDataStorage.setLinkData({
            title: data.title ?? "",
            songFile: data.songFile,
            image: data.image ?? "",
          });
        } else {
          await sessionStorage.linkDataStorage.setLinkData(null);
        }
        setUploadedFiles((prevState) => ({
          ...prevState,
          youtube: data,
        }));
        break;
      case "recordAudio":
        await sessionStorage.recordAudioStorage.setRecordAudioData(
          data.songFile ?? null,
        );
        await sessionStorage.selectedTabStorage.setSelectedFileTab(
          "recordAudio",
        );
        setUploadedFiles((prevState) => ({
          ...prevState,
          recordAudio: data,
        }));
        break;
    }
  };

  const setSelectedTab = async (value: UploadFileTab) => {
    await sessionStorage.selectedTabStorage.setSelectedFileTab(value);
    setUploadedFiles((prevState) => ({
      ...prevState,
      activeTab: value,
    }));
  };

  useEffect(() => {
    sessionStorage
      .getMediaInputsFromStorage()
      .then((result) => setUploadedFiles(result));
  }, [sessionStorage]);

  return (
    <MediaInputContext.Provider
      value={{
        uploadedFiles,
        handleFileChange,
        setSelectedTab,
        licenseManager,
      }}
    >
      {children}
    </MediaInputContext.Provider>
  );
};

export default MediaInputProvider;
